import { clsx } from 'clsx';
import { ExternalLink } from 'lucide-react';

const FALLBACK_LABEL = 'Open in external system';

/**
 * The URL when it is safe to put in an href, otherwise null. The API only
 * accepts https, but this renders data another system supplied, so it never
 * trusts that alone: a javascript: link here would run in the agent's session.
 */
export function safeExternalUrl(url?: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

/** The button text: the integration's own label, then the product's name. */
export function externalLinkLabel(record: {
  externalUrlLabel?: string | null;
  originName?: string | null;
  originType?: string | null;
}): string {
  if (record.externalUrlLabel) return record.externalUrlLabel;
  return record.originType === 'product' && record.originName
    ? `Open in ${record.originName}`
    : FALLBACK_LABEL;
}

interface ExternalRecordLinkProps {
  url?: string | null;
  label: string;
  className?: string;
}

/** Opens the record's page in the external system that sent the link, in a new tab. */
export function ExternalRecordLink({ url, label, className }: ExternalRecordLinkProps) {
  const href = safeExternalUrl(url);
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={`${label} (${new URL(href).hostname})`}
      className={clsx(
        'inline-flex h-[28px] max-w-[240px] items-center gap-1.5 rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-2.5 text-[12.5px] text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)]',
        className,
      )}
    >
      <ExternalLink aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate">{label}</span>
    </a>
  );
}
